/**
 * Component: ReportDetailTable
 * Displays detailed monthly report data in a table format
 */

import { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Pagination,
  TableLoadingRow,
  TableEmptyRow,
} from "@/components/ui/table/table";
import { MoreIcon } from "@/assets/svg";
import type { ReportDetailData } from "../../../../domain/model/DashboardModel";
import { FetchReportDetailUseCase } from "../../../../domain/usecase/FetchReportDetailUseCase";

export interface ReportDetailTableProps {
  data: ReportDetailData | null;
}

export const ReportDetailTable = ({
  data,
}: ReportDetailTableProps): React.JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportData, setReportData] = useState<ReportDetailData | null>(data);
  const itemsPerPage = 10;

  useEffect(() => {
    setReportData(data);
  }, [data]);

  const handlePageChange = async (page: number) => {
    if (page >= 1 && reportData && page <= reportData.paging.totalPages) {
      try {
        setIsLoading(true);
        setCurrentPage(page);
        const newData = await FetchReportDetailUseCase(page, itemsPerPage);
        setReportData(newData);
      } catch (error) {
        console.error("Failed to fetch report detail:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="max-md:p-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            <TableHead>Total Activity</TableHead>
            <TableHead>Total Points Earned</TableHead>
            <TableHead>Voucher Redeemed</TableHead>
            <TableHead>New Users Registered</TableHead>
            <TableHead>Total Users</TableHead>
            <TableHead> </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoadingRow
              colSpan={7}
              message="Loading report details..."
            />
          ) : reportData?.items.length === 0 ? (
            <TableEmptyRow colSpan={7} message="No report details found." />
          ) : (
            reportData?.items.map((row, index) => (
              <TableRow key={row.reportId ?? index}>
                <TableCell>
                  <span className="font-medium">
                    {row.monthName} {row.year}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-medium">
                    {row.totalActivity.toLocaleString("id-ID")}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-medium">
                    {`${row.totalEarned.toLocaleString("id-ID")} points`}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-medium">
                    {row.voucherRedeemed.toLocaleString("id-ID")}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-medium">
                    {row.newUserRegistered.toLocaleString("id-ID")}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-medium">
                    {row.totalUser.toLocaleString("id-ID")}
                  </span>
                </TableCell>
                <TableCell>
                  <button className="bg-transparent border-none text-xl text-gray-500 cursor-pointer px-2 py-1 transition-colors hover:text-[#1e3a5f]">
                    <MoreIcon />
                  </button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Pagination
        currentPage={currentPage}
        totalPages={reportData?.paging?.totalPages ?? 1}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
